/**
 * Details & history: what the report is called for, and every version it has been.
 *
 * Moved out of the design steps. Nobody designs a report in the middle of reading its history,
 * and the history was taking one of the four places the deck has for the design itself. The
 * description lives here; so does the diff against any saved version and the restore.
 */
import { useState } from 'react'
import { GitCompareArrows, RotateCcw } from 'lucide-react'
import { goldBtn, ghostBtn, optionClass } from '../studioUi'
import { metricLabel } from '../helpers'
import type { ColumnId, FilterState, SavedView } from '../types'
import { StudioDialog } from './controls'
import { denseField, denseLabel } from './controlStyles'
import { filterDiffLines } from './filterDiff'
import { DiffList } from './versionDiff'

export function DetailsDialog({
  view,
  description,
  onDescriptionChange,
  draftColumns,
  draftFilters,
  columnLabels,
  busy,
  onRestore,
  onClose,
}: {
  view: SavedView
  description: string
  onDescriptionChange: (next: string) => void
  /** The draft, compared against the chosen version. */
  draftColumns: ColumnId[]
  draftFilters: FilterState
  columnLabels: Map<ColumnId, string>
  busy: boolean
  /** Restores a version with a note. Rejects when the server refuses, having said why. */
  onRestore: (version: number, note: string) => Promise<void>
  onClose: () => void
}) {
  const [compareId, setCompareId] = useState(view.versions[1]?.version ?? view.version)
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null)
  const [restoreNote, setRestoreNote] = useState('')

  const prior = view.versions.find((item) => item.version === compareId) ?? view.versions[0]
  const priorColumns = prior?.columns ?? view.columns
  const priorFilters = prior?.filters ?? view.filters
  const added = draftColumns.filter((id) => !priorColumns.includes(id))
  const removed = priorColumns.filter((id) => !draftColumns.includes(id))
  const filterChanges = filterDiffLines(draftFilters, priorFilters)

  return (
    <StudioDialog label="Details & history" onClose={onClose} width="max-w-4xl">
      <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section>
          <label className="block">
            <span className={denseLabel}>Description</span>
            <textarea
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              rows={3}
              placeholder="What this report is for, and who reads it"
              className={`${denseField} mt-1 resize-y`}
              aria-label="Report description"
            />
          </label>
          <dl className="mt-3 space-y-1.5 text-[11px]">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-stone-500">Owned by</dt>
              <dd className="truncate text-stone-100" title={view.ownerEmail}>
                {view.ownerEmail}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-stone-500">Status</dt>
              <dd className="text-stone-200">
                {view.status} · v{view.version}
              </dd>
            </div>
          </dl>
          <p className="mt-2 text-[10px] leading-relaxed text-stone-600">
            The description is saved with the next version.
          </p>
        </section>

        <section className="flex min-h-0 flex-col">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className={denseLabel}>Version history</span>
            <span className="font-mono text-[9px] text-stone-600">{view.versions.length}</span>
          </div>
          <label className="mb-2 block min-w-0">
            <span className={denseLabel}>Compare the draft to</span>
            <select
              value={compareId}
              onChange={(event) => setCompareId(Number(event.target.value))}
              className={`${denseField} mt-1`}
            >
              {view.versions.map((version) => (
                <option className={optionClass} key={version.version} value={version.version}>
                  v{version.version} · {version.authorEmail}
                </option>
              ))}
            </select>
          </label>
          <div className="mb-2 rounded border border-white/[0.04] bg-white/[0.015] px-2 py-1.5">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] text-stone-400">
              <GitCompareArrows className="h-3 w-3 text-amber-200" />
              Diff vs v{prior?.version ?? view.version}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <DiffList label="Added" values={added.map((id) => columnLabels.get(id) ?? id)} tone="green" />
              <DiffList label="Removed" values={removed.map((id) => columnLabels.get(id) ?? id)} tone="red" />
              <DiffList label="Filters" values={filterChanges} tone="amber" />
              {added.length === 0 && removed.length === 0 && filterChanges.length === 0 ? (
                <div className="text-[11px] text-stone-500">
                  No structural diff · {metricLabel(draftFilters.metric)}
                  {draftFilters.stage !== 'All' ? ` · ${draftFilters.stage}` : ''}
                  {draftFilters.year !== 'All' ? ` · ${draftFilters.year}` : ''}
                </div>
              ) : null}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {view.versions.length === 0 ? (
              <p className="text-[11px] text-stone-500">No versions recorded yet.</p>
            ) : (
              <table className="w-full text-left text-[11px]">
                <tbody>
                  {view.versions.map((version) => {
                    const isCurrent = version.version === view.version
                    const restoring = restoreTarget === version.version
                    return (
                      <tr key={version.version} className="border-b border-white/[0.03] align-top">
                        <td className="w-14 whitespace-nowrap px-1 py-1 font-mono text-stone-300">
                          v{version.version}
                          {isCurrent ? (
                            <span className="ml-1 text-[8px] uppercase tracking-wide text-amber-200/70">now</span>
                          ) : null}
                        </td>
                        <td className="min-w-0 px-1 py-1">
                          <div className="truncate text-stone-400">{version.authorEmail}</div>
                          <div className="truncate text-[10px] text-stone-600">
                            {version.note || 'No note'} · {version.createdAt || '—'}
                          </div>
                          {restoring ? (
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <input
                                value={restoreNote}
                                onChange={(event) => setRestoreNote(event.target.value)}
                                placeholder="Restore note (required)"
                                className={`${denseField} max-w-[14rem]`}
                              />
                              <button
                                type="button"
                                disabled={busy || !restoreNote.trim()}
                                onClick={async () => {
                                  try {
                                    await onRestore(version.version, restoreNote.trim())
                                  } catch {
                                    return // shown in the Studio's banner
                                  }
                                  setRestoreTarget(null)
                                  setRestoreNote('')
                                }}
                                className={goldBtn}
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => {
                                  setRestoreTarget(null)
                                  setRestoreNote('')
                                }}
                                className={ghostBtn}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-1 py-1 text-right">
                          {!isCurrent ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setRestoreTarget(version.version)
                                setRestoreNote(`Restore v${version.version}`)
                              }}
                              className={ghostBtn}
                            >
                              <RotateCcw className="h-3 w-3" /> Restore
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
      <div className="flex shrink-0 items-center justify-end border-t border-amber-300/10 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-stone-600 px-3 py-1.5 text-[11px] text-stone-300 hover:bg-white/5"
        >
          Close
        </button>
      </div>
    </StudioDialog>
  )
}
